// ─── Progressive Screens Magnatrack Shade Manager ────────────────────────────
// Pure logic module — no DOM or Three.js dependencies.
// Tracks mount type and per-side shade state (enabled + deployed position).

const SIDES = ['front', 'back', 'left', 'right'];

export class ShadeManager {
  constructor() {
    this.mountType = 'inside'; // 'inside' (IUH) | 'outside' (OAH)
    this.shades = {
      front:  { enabled: false, position: 0.0 },
      back:   { enabled: false, position: 0.0 },
      left:   { enabled: false, position: 0.0 },
      right:  { enabled: false, position: 0.0 },
    };
  }

  setMountType(type) {
    if (type === 'inside' || type === 'outside') {
      this.mountType = type;
    }
  }

  setShadeEnabled(side, enabled) {
    if (SIDES.includes(side)) {
      this.shades[side].enabled = !!enabled;
    }
  }

  setShadePosition(side, position) {
    if (SIDES.includes(side)) {
      this.shades[side].position = Math.max(0, Math.min(1, position));
    }
  }

  /**
   * Returns only sides that are both enabled AND not blocked by a wall.
   * @param {Object} walls - { front, back, left, right } booleans
   */
  getActiveShades(walls) {
    const result = {};
    for (const side of SIDES) {
      const shade = this.shades[side];
      if (shade.enabled && !walls[side]) {
        result[side] = { ...shade };
      }
    }
    return result;
  }

  /**
   * Called BEFORE setWall() — disables shades on sides that now have a wall.
   * @param {Object} newWalls - the wall state AFTER the toggle
   * @returns {boolean} true if any shade was disabled (signals UI re-render needed)
   */
  onWallChanged(newWalls) {
    let changed = false;
    for (const side of SIDES) {
      if (newWalls[side] && this.shades[side].enabled) {
        this.shades[side].enabled = false;
        changed = true;
      }
    }
    return changed;
  }

  serialize() {
    return {
      mountType: this.mountType,
      sides: {
        front:  { ...this.shades.front },
        back:   { ...this.shades.back },
        left:   { ...this.shades.left },
        right:  { ...this.shades.right },
      },
    };
  }

  apply(data) {
    if (!data) return;
    if (data.mountType) this.setMountType(data.mountType);
    if (data.sides) {
      for (const side of SIDES) {
        if (data.sides[side]) {
          this.shades[side] = {
            enabled:  !!data.sides[side].enabled,
            position: Math.max(0, Math.min(1, data.sides[side].position ?? 0)),
          };
        }
      }
    }
  }
}
